/**
 * BurdenStrip — one chip per household, in the server's order (spz-09-results-views).
 *
 * The strip scrolls horizontally *inside* its card; the page body never does. The
 * worst-off household is marked by border weight, a ring and an explicit label — not by
 * colour alone — so it reads in both themes and for colour-blind viewers.
 */

import type { Mode } from '../lib/types'

/**
 * One household's burden for one region — the element type of
 * `RegionScore.per_household`.
 *
 * TODO: import from #18 (`src/lib/burden/types.ts`, the `region-score` contract) once it
 * merges, and delete this stub. Shape is copied verbatim from that contract; `Mode` is
 * already the real enum from `src/lib/types.ts`, so only the wrapper is local.
 */
export interface HouseholdBurden {
  household_id: string
  best_mode: Mode
  door_to_door_hours: number
  est_cost: number
  within_preference: boolean
  is_worst: boolean
}

const MODE_GLYPH: Record<Mode, string> = {
  drive: '🚗',
  rail: '🚆',
  fly: '✈️',
}

const MODE_WORD: Record<Mode, string> = {
  drive: 'drive',
  rail: 'rail',
  fly: 'fly',
}

/** "5.5 h", "6 h" — one decimal only when it carries information. */
export function formatHours(hours: number): string {
  if (!Number.isFinite(hours)) return '—'
  const rounded = Math.round(hours * 10) / 10
  return `${Number.isInteger(rounded) ? rounded.toFixed(0) : rounded.toFixed(1)} h`
}

/** Whole dollars. Organizers hold the budget — this is a per-household travel cost, not a share. */
export function formatCost(cost: number): string {
  if (!Number.isFinite(cost)) return '—'
  return `$${Math.round(cost).toLocaleString('en-US')}`
}

const STRIP_CSS = `
.spz-strip-wrap {
  max-width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior-x: contain;
  scrollbar-width: thin;
  margin: 0 -0.15rem;
  padding: 0.15rem;
}
.spz-strip { display: flex; gap: 0.5rem; width: max-content; }
.spz-chip {
  flex: 0 0 auto;
  min-width: 8.5rem;
  border: 1px solid var(--spz-line);
  border-radius: 10px;
  background: var(--spz-surface-2);
  padding: 0.5rem 0.6rem;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}
.spz-chip-name { font-size: 0.85rem; font-weight: 700; display: flex; align-items: center; gap: 0.3rem; }
.spz-chip-hours { font-size: 1.05rem; font-weight: 700; line-height: 1.2; }
.spz-chip-meta { font-size: 0.75rem; color: var(--muted); }
.spz-chip-flag {
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}
.spz-chip.is-worst {
  border-width: 2px;
  border-color: var(--spz-line-strong);
  background: color-mix(in oklab, var(--ink) 10%, var(--ground));
  box-shadow: inset 0 0 0 1px var(--spz-line);
}
.spz-chip.is-worst .spz-chip-flag { color: var(--ink); }
.spz-chip.outside-pref {
  border-style: dashed;
  border-color: var(--spz-warn);
  background: var(--spz-warn-soft);
}
.spz-chip.outside-pref.is-worst {
  border-style: solid;
  border-width: 2px;
  border-color: var(--spz-warn);
  box-shadow: inset 0 0 0 2px color-mix(in oklab, var(--ink) 35%, transparent);
}
.spz-chip .spz-warn-text { color: var(--spz-warn); }
`

export interface BurdenStripProps {
  households: HouseholdBurden[]
  householdLabel: (id: string) => string
}

export function BurdenStrip({ households, householdLabel }: BurdenStripProps) {
  if (households.length === 0) {
    return (
      <p className="spz-chip-meta">No household burdens scored for this region yet.</p>
    )
  }
  return (
    <div className="spz-strip-wrap">
      <style href="spz-burden-strip" precedence="spz">
        {STRIP_CSS}
      </style>
      <ul className="spz-strip">
        {households.map((h) => {
          const cls = [
            'spz-chip',
            h.is_worst ? 'is-worst' : '',
            h.within_preference ? '' : 'outside-pref',
          ]
            .filter(Boolean)
            .join(' ')
          return (
            <li className={cls} key={h.household_id}>
              <span className="spz-chip-name">
                <span aria-hidden="true">{MODE_GLYPH[h.best_mode]}</span>
                {householdLabel(h.household_id)}
              </span>
              <span className="spz-chip-hours">{formatHours(h.door_to_door_hours)}</span>
              <span className="spz-chip-meta">
                {MODE_WORD[h.best_mode]} &middot; {formatCost(h.est_cost)}
              </span>
              {h.is_worst ? (
                <span className="spz-chip-flag">
                  <span aria-hidden="true">▲</span> worst-off
                </span>
              ) : null}
              {h.within_preference ? null : (
                <span className="spz-chip-flag spz-warn-text">
                  <span aria-hidden="true">!</span> outside preference
                </span>
              )}
            </li>
          )
        })}
      </ul>
    </div>
  )
}

export default BurdenStrip
